import { CircularProgress, Stack } from '@mui/material';
import { useEffect, useState } from "react";
import Typewriter from 'typewriter-effect';
import AudioFormFillControl from "../components/audio-form-fill-control";
import Header from "../components/header";
import InteractionModel, { InteractionAuthor } from "../data/InteractionModel";
import { CvExtraDataModel, SignupModelHelper } from "../data/SignupModel";
import AmarelinhoService from "../services/AmarelinhoService";
import { AmplitudeService } from '../services/AmplitudeService';

type QuestionStep =
  | 'hasDriverLicense'
  | 'driverLicenseCategory'
  | 'hasMotorcycle'
  | 'hasMotorcycleCargoBox'
  | 'immediateStartAvailability'
  | 'weekendWorkAvailability';

const initialCvExtraData: CvExtraDataModel = {
  hasDriverLicense: null,
  driverLicenseCategory: null,
  hasMotorcycle: null,
  hasMotorcycleCargoBox: null,
  immediateStartAvailability: null,
  weekendWorkAvailability: null
};

const driverLicenseCategories = ['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'];

export default function DadosProfissionais(props: any) {
  const [typed, setTyped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [questionStep, setQuestionStep] = useState<QuestionStep>('hasDriverLicense');
  const [cvExtraData, setCvExtraData] = useState<CvExtraDataModel>(SignupModelHelper.get()?.cvExtraData ?? initialCvExtraData);
  const [audioFilledData, setAudioFilledData] = useState<CvExtraDataModel | null>(null);
  const [interactions, setInteractions] = useState<InteractionModel[]>([]);

  useEffect(() => {
    SignupModelHelper.checkLogin().then(async (userId) => {
      if (!userId) {
        document.location = '#/';
      }
    });

    AmplitudeService.track('Professional Data Screen');
  }, []);

  useEffect(() => {
    if (typed) {
      setTimeout(() => {
        let chat = document.getElementsByClassName('chat')[0];
        chat?.scrollTo(0, chat.scrollHeight);
      }, 500);
    }
  }, [typed, interactions]);

  function getQuestionText(step: QuestionStep): string {
    switch (step) {
      case 'hasDriverLicense':
        return 'Agora preciso saber algumas informações profissionais.<br/><br/>Você <strong>possui CNH</strong>?';
      case 'driverLicenseCategory':
        return 'Qual é a <strong>categoria da sua CNH</strong>?';
      case 'hasMotorcycle':
        return 'Você <strong>possui moto própria</strong>?';
      case 'hasMotorcycleCargoBox':
        return 'E essa moto <strong>possui baú</strong>?';
      case 'immediateStartAvailability':
        return 'Você tem <strong>disponibilidade para início imediato</strong>?';
      case 'weekendWorkAvailability':
        return 'Você tem <strong>disponibilidade para trabalhar aos finais de semana</strong>?';
    }
  }

  function getAnswerLabel(value: boolean | string): string {
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    return value;
  }

  function getNextStep(step: QuestionStep, data: CvExtraDataModel): QuestionStep | null {
    switch (step) {
      case 'hasDriverLicense':
        return data.hasDriverLicense ? 'driverLicenseCategory' : 'immediateStartAvailability';
      case 'driverLicenseCategory':
        return data.driverLicenseCategory?.includes('A') ? 'hasMotorcycle' : 'immediateStartAvailability';
      case 'hasMotorcycle':
        return data.hasMotorcycle ? 'hasMotorcycleCargoBox' : 'immediateStartAvailability';
      case 'hasMotorcycleCargoBox':
        return 'immediateStartAvailability';
      case 'immediateStartAvailability':
        return 'weekendWorkAvailability';
      case 'weekendWorkAvailability':
        return null;
    }
  }

  function getUpdatedData(step: QuestionStep, value: boolean | string): CvExtraDataModel {
    switch (step) {
      case 'hasDriverLicense':
        return {
          ...cvExtraData,
          hasDriverLicense: Boolean(value),
          driverLicenseCategory: Boolean(value) ? cvExtraData.driverLicenseCategory : null,
          hasMotorcycle: null,
          hasMotorcycleCargoBox: null
        };
      case 'driverLicenseCategory':
        return {
          ...cvExtraData,
          driverLicenseCategory: String(value),
          hasMotorcycle: String(value).includes('A') ? cvExtraData.hasMotorcycle : null,
          hasMotorcycleCargoBox: String(value).includes('A') ? cvExtraData.hasMotorcycleCargoBox : null
        };
      case 'hasMotorcycle':
        return {
          ...cvExtraData,
          hasMotorcycle: Boolean(value),
          hasMotorcycleCargoBox: Boolean(value) ? cvExtraData.hasMotorcycleCargoBox : null
        };
      case 'hasMotorcycleCargoBox':
        return {
          ...cvExtraData,
          hasMotorcycleCargoBox: Boolean(value)
        };
      case 'immediateStartAvailability':
        return {
          ...cvExtraData,
          immediateStartAvailability: Boolean(value)
        };
      case 'weekendWorkAvailability':
        return {
          ...cvExtraData,
          weekendWorkAvailability: Boolean(value)
        };
    }
  }

  async function saveCvExtraData(data: CvExtraDataModel, chat: InteractionModel[]) {
    SignupModelHelper.update({ cvExtraData: data });
    const signup = SignupModelHelper.get();
    setLoading(true);
    const result = await AmarelinhoService.saveCvExtraData(signup!);
    setLoading(false);

    if (result.success) {
      AmplitudeService.track('Professional Data Submit', {
        has_driver_license: data.hasDriverLicense,
        driver_license_category: data.driverLicenseCategory,
        has_motorcycle: data.hasMotorcycle,
        has_motorcycle_cargo_box: data.hasMotorcycleCargoBox,
        immediate_start_availability: data.immediateStartAvailability,
        weekend_work_availability: data.weekendWorkAvailability
      });
      document.location = '#/experiencias-profissionais';
    } else {
      setInteractions([...chat, {
        text: result.message ?? 'Estou com problemas para salvar seu cadastro. Favor tente mais tarde.',
        author: InteractionAuthor.AMARELINHO
      }]);
      setTyped(false);
    }
  }

  function answerQuestion(value: boolean | string) {
    const updatedData = getUpdatedData(questionStep, value);
    const chat = [...interactions, {
      text: getQuestionText(questionStep),
      author: InteractionAuthor.AMARELINHO
    }, {
      text: getAnswerLabel(value),
      author: InteractionAuthor.CLIENTE
    }];
    const nextStep = getNextStep(questionStep, updatedData);

    setCvExtraData(updatedData);
    setInteractions(chat);
    setTyped(false);

    if (nextStep) {
      setQuestionStep(nextStep);
    } else {
      saveCvExtraData(updatedData, chat);
    }
  }

  function getOptions() {
    if (questionStep === 'driverLicenseCategory') {
      return driverLicenseCategories.map(category => ({
        label: category,
        value: category
      }));
    }

    return [
      { label: 'Sim', value: true },
      { label: 'Não', value: false }
    ];
  }

  function getBooleanValue(value: string): boolean | null {
    if (value === 'true' || value.toLowerCase() === 'sim') return true;
    if (value === 'false' || value.toLowerCase() === 'nao' || value.toLowerCase() === 'não') return false;
    return null;
  }

  function applyProfessionalDataFromAudio(values: Record<string, string>) {
    const nextData = {
      ...cvExtraData,
      hasDriverLicense: getBooleanValue(values.hasDriverLicense),
      driverLicenseCategory: values.driverLicenseCategory || null,
      hasMotorcycle: getBooleanValue(values.hasMotorcycle),
      hasMotorcycleCargoBox: cvExtraData.hasMotorcycleCargoBox,
      immediateStartAvailability: getBooleanValue(values.immediateStartAvailability),
      weekendWorkAvailability: cvExtraData.weekendWorkAvailability,
      travelAvailability: getBooleanValue(values.travelAvailability)
    };

    setCvExtraData(nextData);
    setAudioFilledData(nextData);
  }

  return (
    <>
      <Header step={ 1 } total={ 1 } title="Dados profissionais" previous="#/objetivos-profissionais" finished={ false }></Header>

      <div className="chat-container">
        <div className="chat">
          <div className="container">
            <div className="row">
              <div className="col-12 d-flex flex-column align-items-stretch justify-content-between">
                {
                  interactions.map((interaction, index) => {
                    let objRet;

                    if (interaction.author === InteractionAuthor.AMARELINHO) {
                      objRet = <div className="chat-item" key={ index }>
                        <img src="images/ico-chat-amarelinho.svg" alt="Amarelinho" />
                        <div className="chat-item-content">
                          <p dangerouslySetInnerHTML={ { __html: interaction.text } }></p>
                        </div>
                      </div>

                    } else if (interaction.author === InteractionAuthor.CLIENTE) {
                      objRet = <div className="chat-item chat-item-user" key={ index }>
                        <img src="images/ico-chat-user.svg" alt="Usuário" />
                        <div className="chat-item-content">
                          <p>{ interaction.text }</p>
                        </div>
                      </div>
                    }

                    return objRet;
                  })
                }

                { !loading &&
                  <div className="chat-item" key={ questionStep }>
                    <img src="images/ico-chat-amarelinho.svg" alt="Amarelinho" />
                    <div className="chat-item-content">
                      <Typewriter
                        onInit={ (typewriter) => {
                          typewriter
                            .changeDelay(5)
                            .typeString(getQuestionText(questionStep))
                            .callFunction(() => {
                              setTyped(true)
                            })
                            .start();
                        } } />
                    </div>
                  </div>
                }

                { !loading &&
                  <div className="audio-inline-action">
                    <AudioFormFillControl
                      fields={ [
                        { key: 'hasDriverLicense', label: 'Possui CNH' },
                        { key: 'driverLicenseCategory', label: 'Categoria da CNH' },
                        { key: 'hasMotorcycle', label: 'Moto própria' },
                        { key: 'immediateStartAvailability', label: 'Início imediato' },
                        { key: 'travelAvailability', label: 'Disponibilidade para viajar' },
                      ] }
                      onApply={ applyProfessionalDataFromAudio }
                      buttonTitle="Preencher dados profissionais por áudio"
                    />
                    <span>Preencher esta etapa por áudio</span>
                  </div>
                }

                { audioFilledData &&
                  <div className="audio-filled-actions">
                    <p>Dados profissionais preenchidos por áudio e revisados. Salve somente se estiver tudo certo.</p>
                    <button className="btn btn-outline" type="button" onClick={ () => setAudioFilledData(null) }>Continuar manualmente</button>
                    <button className="btn btn-secondary" type="button" onClick={ () => saveCvExtraData(audioFilledData, interactions) }>Salvar respostas por áudio</button>
                  </div>
                }
              </div>
            </div>

            { typed && !loading &&
              <div className={ `professional-list cv-extra-data-options${questionStep === 'driverLicenseCategory' ? ' cv-extra-data-category-options' : ''}` }>
                {
                  getOptions().map((option) => {
                    return <div className="professional-item" key={ String(option.value) }>
                      <button className="btn btn-outline" onClick={ () => answerQuestion(option.value) }>{ option.label }</button>
                    </div>;
                  })
                }
              </div>
            }
          </div>
        </div>

        { loading &&
          <footer className="footer-answer">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="footer-answer-holder d-flex align-items-center justify-content-between">
                    <Stack sx={ { color: '#6F5192' } } justifyContent="center" alignItems="center">
                      <CircularProgress color="inherit" />
                    </Stack>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        }
      </div>
    </>);
}
